import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Radio } from "lucide-react";
import { fmtUSDStrict } from "@/lib/format";

const TONE = {
  CONNECTED: { background: "rgba(14,143,110,0.12)", color: "#33265A" },
  ERROR: { background: "rgba(200,60,60,0.12)", color: "#A32222" },
};

/** Admin → Connectivity: which venue actually executes orders. */
export const AlpacaCard = () => {
  const [s, setS] = useState(null);
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    api.get("/venues/alpaca/status").then((r) => setS(r.data)).catch(() => {});
    api.get("/market/feed/status").then((r) => setFeed(r.data)).catch(() => {});
  }, []);
  if (!s) return null;
  const short = s.status?.split(" ")[0];

  return (
    <Card className="p-5 shadow-premium" data-testid="alpaca-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Radio className="h-4 w-4" />Alpaca execution venue
        </div>
        <Badge style={TONE[short] || { background: "rgba(120,120,120,0.12)", color: "#555" }}
          data-testid="alpaca-status-badge">
          {s.configured ? `${short} · ${s.mode}` : "Not connected"}
        </Badge>
      </div>

      {s.account ? (
        <div className="space-y-2">
          {[["Account", s.account.account_number], ["Status", s.account.status],
            ["Equity", fmtUSDStrict(s.account.equity)], ["Buying power", fmtUSDStrict(s.account.buying_power)]]
            .map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded-lg border px-3 py-2">
                <span className="text-sm text-muted-foreground">{k}</span>
                <span className="text-sm numeric">{v}</span>
              </div>
            ))}
          <p className="text-xs text-muted-foreground leading-relaxed pt-1">
            US stocks and ETFs route to Alpaca ({s.mode}). Gulf, EU and Asian listings, commodities
            and forex stay on the Tharwy engine.
          </p>
        </div>
      ) : (
        <div className="rounded-lg bg-secondary/60 p-3">
          <div className="text-sm font-medium">Orders are executing on the Tharwy engine</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {s.error ? `Alpaca replied: ${s.error}` : s.note}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Routing rule: <span className="font-mono">{s.routing}</span>
            {s.key_id_suffix ? ` · key ending ${s.key_id_suffix}` : ""}
          </p>
        </div>
      )}

      {feed && (
        <div className="mt-3 rounded-lg border px-3 py-2.5" data-testid="alpaca-feed-status">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm">Price feed</span>
            <span className="text-sm font-medium">
              {feed.streaming ? `Alpaca ${feed.feed?.toUpperCase()} · ${feed.symbol_count} symbols` : "Tharwy simulator"}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {feed.streaming
              ? `${feed.ticks_received.toLocaleString()} ticks received · live on ${feed.live_symbols.length} symbols. Clients receive them over one shared websocket.`
              : feed.note}
          </p>
        </div>
      )}
    </Card>
  );
};
